import { useState } from "react"
import { Package } from "@phosphor-icons/react"

import { toast } from "react-toastify"

import { InlineAlert, RadioGroup, RadioGroupItem } from "~/components/design-system/components"
import { pluralize } from "~/utils/formatting"
import { showErrorToast } from "~/utils/toaster"
import { PackageFolderPage } from "./PackageFolderPage"
import { PackageFolderTree, type PackageSelection } from "./PackageFolderTree"
import PackageWizard, { type PackageWizardMode } from "./PackageWizard"
import { PackagePage } from "./PackagePage"
import { noteRunAfterCurrent, PackageStoresCommandbar } from "./setup/setupDrafts"
import {
  useAllocationMutations,
  useAllocationPackageFolders,
  useAllocationPackages,
} from "./useAllocationData"
import type { PackageFolder, RangePackage } from "./types"

// ─────────────────────────────────────────────────────────────────────────────
// RangeTab (default export)
// ─────────────────────────────────────────────────────────────────────────────

type MoveStores = "keep" | "follow"
type PendingMove = { pkg: RangePackage; folder: PackageFolder; stores: MoveStores }

function MovePackageConfirmation({
  move,
  saving,
  onChoose,
  onCancel,
  onApply,
}: {
  move: PendingMove
  saving: boolean
  onChoose: (stores: MoveStores) => void
  onCancel: () => void
  onApply: () => void
}) {
  const { pkg, folder } = move
  return (
    <InlineAlert
      tone="info"
      size="block"
      title={`Move “${pkg.name}” to ${folder.name}?`}
      description={
        <div className="flex flex-col gap-2">
          <span>{pluralize(pkg.productIds.length, "variant")} move with it. Stores:</span>
          <RadioGroup
            value={move.stores}
            onValueChange={(value) => {
              if (value === "keep" || value === "follow") onChoose(value)
            }}
          >
            <label className="flex items-start gap-2">
              <RadioGroupItem value="keep" className="mt-0.5" />
              <span className="label-base text-fg-base">
                Keep its store assignment ({pluralize(pkg.storeIds.length, "store")})
              </span>
            </label>
            <label className="flex items-start gap-2">
              <RadioGroupItem value="follow" className="mt-0.5" />
              <span className="label-base text-fg-base">
                {`Follow ${folder.name}’s stores (${pluralize(folder.storeIds.length, "store")})`}
              </span>
            </label>
          </RadioGroup>
          <span className="paragraph-sm text-fg-muted">
            The package keeps its own dates, variants, SKUs and hand-set variant dates.
          </span>
        </div>
      }
      secondaryAction={{ label: "Cancel", onClick: onCancel }}
      action={{ label: "Move", onClick: onApply, requires: "edit", disabled: saving }}
    />
  )
}

export default function RangeTab() {
  const [selection, setSelection] = useState<PackageSelection>(null)
  const [wizardMode, setWizardMode] = useState<PackageWizardMode | null>(null)

  const { packages, isLoading } = useAllocationPackages()
  const { folders, isLoading: foldersLoading } = useAllocationPackageFolders()
  const { savePackagePage, savePackageFolder } = useAllocationMutations()
  const selectedPkg =
    selection?.kind === "package" ? packages.find((p) => p.id === selection.id) : undefined
  const selectedFolder =
    selection?.kind === "folder" ? folders.find((f) => f.id === selection.id) : undefined
  const selectPackage = (id: string) => setSelection({ kind: "package", id })
  const selectFolder = (id: string) => setSelection({ kind: "folder", id })
  const onSaved = (result: { run: Parameters<typeof noteRunAfterCurrent>[0] }) =>
    noteRunAfterCurrent(result.run)

  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null)
  const commitMove = (packageId: string, folderId: string | null, stores: MoveStores) =>
    savePackagePage.mutate(
      { op: { kind: "movePackage", packageId, folderId, stores } },
      {
        onSuccess: (result) => {
          setPendingMove(null)
          onSaved(result)
          const folder = folders.find((other) => other.id === folderId)
          toast.success(folder ? `Moved to ${folder.name}` : "Moved out of its folder")
        },
        onError: showErrorToast,
      },
    )
  // Into another folder asks what happens to its stores; out of every folder keeps them.
  const movePackage = (packageId: string, folderId: string | null) => {
    const pkg = packages.find((other) => other.id === packageId)
    const folder = folders.find((other) => other.id === folderId)
    if (!pkg || !folder) return commitMove(packageId, folderId, "keep")
    setPendingMove({ pkg, folder, stores: "keep" })
  }
  const moveFolder = (folderId: string, parentId: string | null) =>
    savePackageFolder.mutate(
      { op: { kind: "moveFolder", folderId, parentId } },
      { onSuccess: onSaved, onError: showErrorToast },
    )
  const newFolder = (name: string) => {
    const folderId = crypto.randomUUID()
    savePackageFolder.mutate(
      { op: { kind: "createFolder", folderId, parentId: null, window: null, name } },
      { onSuccess: () => selectFolder(folderId), onError: showErrorToast },
    )
  }

  return (
    <div className="flex h-full flex-col">
      {isLoading || foldersLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sm text-fg-subtle">Loading packages...</p>
        </div>
      ) : (
        <div className="flex flex-1 overflow-hidden">
          <PackageFolderTree
            packages={packages}
            folders={folders}
            selection={selection}
            onSelect={setSelection}
            onNewPackage={() =>
              // A package created while a folder is open goes into that folder.
              setWizardMode({ kind: "create", folderId: selectedFolder?.id ?? null })
            }
            onNewFolder={newFolder}
            onMovePackage={movePackage}
            onMoveFolder={moveFolder}
          />
          <div className="flex flex-1 flex-col overflow-hidden">
            {pendingMove && (
              <div className="px-6 pt-6">
                <MovePackageConfirmation
                  move={pendingMove}
                  saving={savePackagePage.isPending}
                  onChoose={(stores) => setPendingMove({ ...pendingMove, stores })}
                  onCancel={() => setPendingMove(null)}
                  onApply={() =>
                    commitMove(pendingMove.pkg.id, pendingMove.folder.id, pendingMove.stores)
                  }
                />
              </div>
            )}
            {selectedPkg && (
              <PackagePage
                key={selectedPkg.id}
                pkg={selectedPkg}
                packages={packages}
                folders={folders}
                onEditRange={() => setWizardMode({ kind: "editRange", pkg: selectedPkg })}
                onSelectPackage={selectPackage}
                onSelectFolder={selectFolder}
              />
            )}
            {selectedFolder && (
              <PackageFolderPage
                key={selectedFolder.id}
                folder={selectedFolder}
                folders={folders}
                packages={packages}
                onSelectFolder={selectFolder}
                onSelectPackage={selectPackage}
              />
            )}
            {!selectedPkg && !selectedFolder && (
              <div className="flex flex-1 items-center justify-center">
                <div className="text-center">
                  <Package size={40} className="mx-auto text-fg-subtle" />
                  <p className="mt-2 text-sm text-fg-subtle">
                    {packages.length + folders.length === 0
                      ? "No packages yet"
                      : "Select a folder or package to view details"}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {wizardMode && (
        <PackageWizard
          mode={wizardMode}
          onClose={() => setWizardMode(null)}
          onCreated={selectPackage}
        />
      )}

      <PackageStoresCommandbar />
    </div>
  )
}
